import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import { getCustomerQueryOptions } from '@/features/customers/query-options'

/** 荷主マスタが持つ荷役作業の既定値（#4741）。null は「未設定」を表す */
export type CustomerCargoHandlingDefaults = {
  isLoadingWorkOutsourced: boolean | null
  loadingWorkMinutes: number | null
  isUnloadingWorkOutsourced: boolean | null
  unloadingWorkMinutes: number | null
}

/**
 * 荷主 (Customer) 選択時に、その荷主の荷役作業の既定値を配送依頼の入力欄へ
 * 自動投入するための hook (#4741)。
 *
 * `useCustomerDefaultAdditionals`（付帯費用の既定）と同じ荷主データを読むが、
 * 投入先も適用単位も異なるため hook を分けている。`getCustomerQueryOptions` は
 * 同じ query key なので、両方を呼んでも荷主の fetch は 1 回で済む。
 *
 * 備考（#4893 で追加）は既定値の対象外である。荷主ごとに固定の文言を毎回
 * 差し込む運用は想定されておらず、Biz 要望も「委託の有無と予定作業時間」だった
 * （[出典](https://simount.backlog.com/view/VOC_PJ-618#comment-791271615)）。
 *
 * 作成系モーダルでの利用を想定しており、`customerId` 初期値は null。
 * よって「初期ロード済みデータへの誤適用」は起きない。
 */
export const useCustomerCargoHandlingDefaults = ({
  tenantKey,
  customerId,
  enabled = true,
  onApply
}: {
  tenantKey: string
  customerId: number | null | undefined
  enabled?: boolean
  onApply: (defaults: CustomerCargoHandlingDefaults) => void
}): void => {
  const { data } = useQuery({
    ...getCustomerQueryOptions({ tenantKey, customerId: customerId ?? 0 }),
    enabled: enabled && customerId != null,
    retry: false
  })

  const lastAppliedRef = useRef<number | null>(null)

  useEffect(() => {
    if (customerId == null) {
      // 荷主クリア時は次回選択で再適用できるようリセット
      lastAppliedRef.current = null
      return
    }
    if (lastAppliedRef.current === customerId) return
    // fetch 中 / 古い荷主のデータの間は待つ
    if (!data || data.id !== customerId) return

    lastAppliedRef.current = customerId
    // 4項目は #4741 で追加されたフィールド。旧 BE では欠落しうるため
    // ?? null でガードする（undefined を渡すと RHF が「未変更」と解釈する）。
    onApply({
      isLoadingWorkOutsourced: data.defaultIsLoadingWorkOutsourced ?? null,
      loadingWorkMinutes: data.defaultLoadingWorkMinutes ?? null,
      isUnloadingWorkOutsourced: data.defaultIsUnloadingWorkOutsourced ?? null,
      unloadingWorkMinutes: data.defaultUnloadingWorkMinutes ?? null
    })
  }, [customerId, data, onApply])
}
